'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface User {
  userId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const fetchUser = async () => {
    try {
      const res = await fetch('/api/v2/auth/me');
      if (res.ok) {
        const result = await res.json();
        const data = result.data || result;
        setUser(data.user);
      } else {
        // Don't log 401 errors as they're expected for unauthenticated users
        if (res.status !== 401) {
          console.error('Error fetching user:', res.status, res.statusText);
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    await fetchUser();
  };

  const logout = async () => {
    try {
      await fetch('/api/v2/auth/logout', { method: 'POST' });
      setUser(null);
      
      // Trigger event to notify other components
      window.dispatchEvent(new CustomEvent('userLoggedOut'));
      
      // Set storage event to notify other tabs
      localStorage.setItem('auth_logout', Date.now().toString());
      localStorage.removeItem('auth_logout');
      
      router.push('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear state and redirect
      setUser(null);
      window.dispatchEvent(new CustomEvent('userLoggedOut'));
      router.push('/');
    }
  };

  useEffect(() => {
    // Skip API calls for search engine crawlers to avoid 499 errors in Google Search Console
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('googlebot') || ua.includes('bingbot') || ua.includes('yandex') || ua.includes('baiduspider')) {
      setLoading(false);
      return;
    }
    // Only fetch user once when the app loads
    fetchUser();

    // Listen for logout events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_logout') {
        setUser(null);
        router.push('/');
      }
    };

    // Listen for logout events within the same tab
    const handleLogoutEvent = () => {
      setUser(null);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedOut', handleLogoutEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedOut', handleLogoutEvent);
    };
  }, [router]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook for role-based access
export const useRequireAuth = (allowedRoles?: string[]) => {
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        router.push('/login');
        return;
      }
      
      if (allowedRoles && !allowedRoles.includes(user.role)) {
        router.push('/');
        return;
      }
    }
  }, [user, loading, allowedRoles, router]);

  return { user, loading };
};